import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import {
  StickyNote, Trash2, Gift, ArrowRightLeft, AlertTriangle, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : '—';

/* ─────────────────────────────────────────────────────────────────────── */
/* Notes block for an order detail modal — list + add + delete             */
/* ─────────────────────────────────────────────────────────────────────── */
export function OrderNotes({ orderId, initialNotes = [], onUpdate, token }) {
  const [notes, setNotes] = useState(initialNotes);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { setNotes(initialNotes); }, [initialNotes]);

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/orders/${orderId}/notes`, { text: text.trim() }, { headers });
      const next = [...notes, r.data];
      setNotes(next);
      setText('');
      onUpdate?.(next);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save note');
    } finally { setBusy(false); }
  };

  const remove = async (note_id) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/admin/orders/${orderId}/notes/${note_id}`, { headers });
      const next = notes.filter(n => n.note_id !== note_id);
      setNotes(next);
      onUpdate?.(next);
    } catch (e) {
      toast.error('Could not delete note');
    }
  };

  return (
    <div data-testid="order-notes-block">
      <h3 className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
        <StickyNote className="h-3.5 w-3.5" /> Internal Notes ({notes.length})
      </h3>
      <div className="space-y-1.5 mb-2 max-h-40 overflow-y-auto">
        {notes.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No notes yet — add one to keep a record for the team.</p>
        ) : notes.map((n) => (
          <div key={n.note_id} className="flex items-start justify-between gap-2 p-2 rounded-md bg-muted/40 border border-border" data-testid={`note-${n.note_id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-xs whitespace-pre-wrap">{n.text}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {n.author_name} · {fmtDate(n.created_at)}
                {n.type ? <span className="ml-2 px-1.5 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 text-[9px] uppercase">{n.type}</span> : null}
              </p>
            </div>
            <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 text-muted-foreground hover:text-rose-400" onClick={() => remove(n.note_id)} data-testid={`note-del-${n.note_id}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add an internal note (only the team sees this)…"
          rows={2}
          className="text-xs resize-none"
          data-testid="note-input"
        />
        <Button size="sm" onClick={add} disabled={busy || !text.trim()} data-testid="note-add-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Add'}
        </Button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Dispute banner — slot at top of order detail when chargeback flagged    */
/* ─────────────────────────────────────────────────────────────────────── */
export function DisputeBanner({ order }) {
  if (!order?.is_disputed) return null;
  return (
    <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 flex items-start gap-2" data-testid="dispute-banner">
      <AlertTriangle className="h-5 w-5 text-rose-500 shrink-0 mt-0.5" />
      <div className="text-xs">
        <p className="font-semibold text-rose-300">Chargeback / Dispute — {order.dispute_status}</p>
        <p className="text-rose-300/80 mt-0.5">
          Reason: <strong>{order.dispute_reason || 'unspecified'}</strong>
          {order.dispute_amount_cents ? ` · Amount: $${(order.dispute_amount_cents / 100).toFixed(2)}` : ''}
        </p>
        <p className="text-rose-300/70 mt-1">Respond inside the Stripe dashboard before the dispute window closes (typically 7-21 days).</p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Transfer ticket dialog — re-assign one or more codes to a new buyer     */
/* ─────────────────────────────────────────────────────────────────────── */
export function TransferDialog({ open, onOpenChange, order, token, onTransferred }) {
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [moveAddons, setMoveAddons] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setSelected([]); setName(''); setEmail(''); setPhone(''); setMoveAddons(false); }
  }, [open]);

  const toggleCode = (code) => setSelected(s => s.includes(code) ? s.filter(c => c !== code) : [...s, code]);

  const submit = async () => {
    if (!selected.length) return toast.error('Select at least one ticket to transfer');
    if (!name.trim() || !email.includes('@')) return toast.error('New buyer name + valid email required');
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/orders/${order.order_id}/transfer`,
        {
          ticket_codes: selected,
          new_buyer_name: name.trim(),
          new_buyer_email: email.trim(),
          new_buyer_phone: phone.trim(),
          transfer_addons: moveAddons,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Transferred ${selected.length} ticket(s)${moveAddons && addons.length ? ' + add-ons' : ''} to ${email}`);
      onTransferred?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transfer failed');
    } finally { setBusy(false); }
  };

  const eligible = (order?.tickets || []).filter(t => t.status !== 'checked_in' && t.status !== 'refunded');
  const addons = (order?.items || []).filter(i => i.is_addon);
  const addonsHolder = order?.addons_holder;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="transfer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="h-5 w-5 text-indigo-500" /> Transfer tickets</DialogTitle>
          <DialogDescription className="text-xs">Re-assign tickets to a different buyer. They'll receive a fresh confirmation email with wallet access.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Tickets to transfer ({selected.length} selected)</p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {eligible.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No eligible tickets — checked-in and refunded tickets can't be transferred.</p>
              ) : eligible.map(t => (
                <label key={t.ticket_code} className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer ${t.transferred_at ? 'border-amber-500/40 bg-amber-500/5' : 'border-border hover:bg-muted/40'}`}>
                  <input
                    type="checkbox"
                    checked={selected.includes(t.ticket_code)}
                    onChange={() => toggleCode(t.ticket_code)}
                    data-testid={`transfer-code-${t.ticket_code}`}
                  />
                  <span className="font-mono text-xs">{t.ticket_code}</span>
                  <span className="text-[11px] text-muted-foreground">{t.ticket_type_name}</span>
                  {t.transferred_at ? (
                    <span className="ml-auto text-[10px] font-semibold text-amber-400 truncate max-w-[150px]" data-testid={`transferred-tag-${t.ticket_code}`}>
                      already transferred → {t.buyer_email}
                    </span>
                  ) : (
                    <span className="ml-auto text-[10px] text-muted-foreground truncate max-w-[130px]">holder: {t.buyer_name || order?.buyer_name}</span>
                  )}
                </label>
              ))}
            </div>
          </div>
          {addons.length > 0 && (
            <div className="rounded-md border border-indigo-500/30 bg-indigo-500/5 p-3 space-y-2" data-testid="transfer-addons-section">
              <p className="text-[11px] uppercase tracking-wider text-indigo-300 font-semibold">Add-ons / upgrades on this order</p>
              <ul className="text-xs space-y-0.5">
                {addons.map((a, i) => (
                  <li key={i}>{a.name}{a.variation_name ? ` — ${a.variation_name}` : ''} × {a.quantity}</li>
                ))}
              </ul>
              <p className="text-[10px] text-muted-foreground">
                Add-ons aren't tied to a specific ticket — currently with <strong>{addonsHolder ? `${addonsHolder.name} (${addonsHolder.email})` : 'the original buyer'}</strong>.
              </p>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={moveAddons} onChange={e => setMoveAddons(e.target.checked)} data-testid="transfer-addons-checkbox" />
                Also transfer the add-ons to the new holder (they'll be listed on their email)
              </label>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">New buyer name</label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe" data-testid="transfer-name" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Email</label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@example.com" data-testid="transfer-email" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Phone (optional)</label>
              <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="555-1234" data-testid="transfer-phone" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !selected.length} data-testid="transfer-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRightLeft className="h-4 w-4 mr-2" />}
            Transfer {selected.length || ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Issue comp tickets dialog                                               */
/* ─────────────────────────────────────────────────────────────────────── */
export function CompTicketsDialog({ open, onOpenChange, eventId, token, onIssued }) {
  const [ticketTypes, setTicketTypes] = useState([]);
  const [tt, setTt] = useState('');
  const [qty, setQty] = useState(1);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(''); setEmail(''); setPhone(''); setNote(''); setQty(1);
    axios.get(`${API}/events/${eventId}/ticket-types`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        // Endpoint returns { ticket_types: [...] } not a flat array
        const raw = Array.isArray(r.data) ? r.data : (r.data?.ticket_types || []);
        const list = raw.filter(x => x.is_active);
        setTicketTypes(list);
        if (list.length) setTt(list[0].ticket_type_id);
      })
      .catch(() => toast.error('Could not load ticket types'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eventId]);

  const submit = async () => {
    if (!name.trim() || !email.includes('@')) return toast.error('Name + valid email required');
    if (!tt) return toast.error('Pick a ticket type');
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/events/${eventId}/comp-tickets`,
        {
          buyer_name: name.trim(),
          buyer_email: email.trim(),
          buyer_phone: phone.trim(),
          items: [{ ticket_type_id: tt, quantity: qty }],
          note: note.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Issued ${r.data.tickets_issued} comp ticket(s) to ${email}`);
      onIssued?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not issue comp tickets');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="comp-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Gift className="h-5 w-5 text-fuchsia-500" /> Issue comp tickets</DialogTitle>
          <DialogDescription className="text-xs">Free tickets for press, VIPs, give-aways, or vendor passes. The recipient gets a real confirmation email with wallet access.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Recipient name</label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="VIP Press Recipient" data-testid="comp-name" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Email</label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="press@outlet.com" data-testid="comp-email" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Phone (optional)</label>
              <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="555-9999" data-testid="comp-phone" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Ticket type</label>
              <Select value={tt} onValueChange={setTt}>
                <SelectTrigger data-testid="comp-ticket-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {ticketTypes.map(x => (
                    <SelectItem key={x.ticket_type_id} value={x.ticket_type_id}>{x.name} (retail ${x.price})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Quantity</label>
              <Input type="number" min={1} max={50} value={qty} onChange={e => setQty(parseInt(e.target.value, 10) || 1)} data-testid="comp-qty" />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Reason / context (saved as note)</label>
            <Textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="Press list — Denver Post, etc." className="text-xs" data-testid="comp-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="comp-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Gift className="h-4 w-4 mr-2" />}
            Issue {qty} comp{qty > 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Sales velocity chart — pure SVG, no chart library (keeps bundle small)  */
/* ─────────────────────────────────────────────────────────────────────── */
export function SalesVelocityChart({ eventId, token }) {
  const [series, setSeries] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/events/${eventId}/sales-velocity`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setSeries(r.data.series || []))
      .catch(() => setSeries([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  if (series === null) {
    return (
      <Card className="bg-muted/40" data-testid="velocity-card-loading">
        <CardContent className="p-4 flex items-center gap-2 text-muted-foreground text-xs">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading sales velocity…
        </CardContent>
      </Card>
    );
  }
  if (series.length === 0) return null;

  // Build a simple sparkline SVG. Width is responsive via viewBox.
  const W = 1000, H = 120, P = 24;
  const maxT = Math.max(1, ...series.map(s => s.tickets));
  const stepX = (W - P * 2) / Math.max(1, series.length - 1);
  const points = series.map((s, i) => {
    const x = P + i * stepX;
    const y = H - P - (s.tickets / maxT) * (H - P * 2);
    return [x, y, s];
  });
  const polyline = points.map(p => `${p[0]},${p[1]}`).join(' ');
  const area = `M ${P},${H - P} L ${polyline.split(' ').join(' L ')} L ${P + (series.length - 1) * stepX},${H - P} Z`;
  const totalTickets = series.reduce((a, b) => a + b.tickets, 0);
  const peakDay = series.reduce((best, s) => s.tickets > best.tickets ? s : best, series[0]);

  return (
    <Card className="bg-muted/40 border-border" data-testid="velocity-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Sales Velocity · last {series.length} day{series.length === 1 ? '' : 's'}</p>
          <p className="text-[11px] text-muted-foreground">
            <span className="font-semibold text-foreground">{totalTickets}</span> tickets · peak {peakDay.tickets} on {peakDay.date}
          </p>
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-24" preserveAspectRatio="none">
          <defs>
            <linearGradient id="velocity-grad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#6366f1" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill="url(#velocity-grad)" />
          <polyline points={polyline} fill="none" stroke="#818cf8" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          {points.map(([x, y, s], i) => (
            <circle key={i} cx={x} cy={y} r={s.tickets ? 2.5 : 1} fill={s.tickets ? '#a5b4fc' : '#475569'}>
              <title>{`${s.date}: ${s.tickets} tickets, $${(s.gross_cents / 100).toFixed(2)}`}</title>
            </circle>
          ))}
        </svg>
        <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
          <span>{series[0].date}</span>
          <span>{series[series.length - 1].date}</span>
        </div>
      </CardContent>
    </Card>
  );
}
